import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { setHighlights, isLoading, setHistory, setHoldings } from '../../../redux/portfolioReducer';
// import { useNotifications } from 'components/Hooks/useNotifications';
import HorizontalWallet from '../../../components/HorizontalWallet';
import Statistics from '../../../components/Statistics';
import LineChart from '../../../components/LineChart';
import PortfolioHoldings from '../../../components/PortfolioHoldings';
import PnLTransactions from '../../../components/PnL';
import styles from './Overview.module.sass';
import Spinner from 'components/Spinner';
import { getPortfolioHighlight, getPortfolioHistory, getPortfolio, getPnLData } from '../../../services/portfolio';
import { useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';

interface PnLData {
  token_address: string;
  wallet_address: string;
  symbol: string;
  chain: string;
  realized_profit_usd: string;
  realized_profit_percentage: number;
  avg_buy_price_usd: string;
  avg_sell_price_usd: string;
  total_usd_invested: string;
  logo: string;
  name: string;
  decimals: string;
  total_tokens_bought: string;
  total_tokens_sold: string;
  total_buys: number;
  total_sells: number;
  total_sold_usd: string;
  count_of_trades: number;
  avg_cost_of_quantity_sold: string;
  possible_spam: boolean;
}

function OverviewPage() {
  const auth = useAuth()
  const dispatch = useDispatch();
  // const { showNotification } = useNotifications();

  // Split selectors for better performance
  const loading = useSelector((state: RootState) => state.portfolio.loading);
  const wallets = useSelector((state: RootState) => state.user.wallets);
  const highlights = useSelector((state: RootState) => state.portfolio.highlights);
  const history = useSelector((state: RootState) => state.portfolio.history ?? []);
  const holdings = useSelector((state: RootState) => 
    Array.isArray(state.portfolio.holdings) ? state.portfolio.holdings : []
  );
  const pnlList = useSelector((state: RootState) => 
    Array.isArray(state.portfolio.pnl) ? state.portfolio.pnl : []
  ) as PnLData[];
  const dataDuration = useSelector((state: RootState) => state.user.dataDuration);

  // Statistics state
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [prevWallet, setPrevWallet] = useState<string>('');
  const [prevPortfolioValue, setPrevPortfolioValue] = useState<number | null>(null);

  // PnL state
  const [pnlActiveIndex, setPnlActiveIndex] = useState(0);
  const [pnlIsPaused, setPnlIsPaused] = useState(false);
  const [prevPnLWallet, setPrevPnLWallet] = useState<string>('');

  const selectedWallet = wallets?.[0] || '';

  // Filter PnL list based on selected wallet
  const filteredPnlList = useMemo(() => {
    if (!selectedWallet) return [];
    return pnlList.filter(
      (obj: PnLData) => obj.wallet_address.toLowerCase() === selectedWallet.toLowerCase()
    );
  }, [pnlList, selectedWallet]);

  // Reset PnL active index when filtered list changes
  useEffect(() => {
    setPnlActiveIndex(0);
  }, [filteredPnlList]);

  // PnL auto-rotation
  useEffect(() => {
    if (!pnlIsPaused && filteredPnlList.length > 0) {
      const timer = setInterval(() => {
        setPnlActiveIndex((current) => (current + 1) % filteredPnlList.length);
      }, 6000);
      
      return () => clearInterval(timer);
    }
  }, [pnlIsPaused, filteredPnlList.length]);

  const handlePnLNavigateCards = (direction: 'next' | 'prev') => {
    const totalCards = filteredPnlList.length;
    if (direction === 'next') {
      setPnlActiveIndex((current) => (current + 1) % totalCards);
    } else {
      setPnlActiveIndex((current) => (current - 1 + totalCards) % totalCards);
    }
  };

  const handleCardExpand = (title: string) => {
    setExpandedCard(expandedCard === title ? null : title);
  };

  // Process chart data with memoization
  const processedChartData = useMemo(() => {
    if (!history?.length) return [];
    
    // Process timestamps to maintain their 
    // original format while ensuring local time display
    return history.map(dataPoint => {
      // Keep the timestamp in its original format
      // This ensures XAxis formatting in LineChart component works correctly
      return {
        ...dataPoint,
        // Store original timestamp for reference if needed
        originalTimestamp: dataPoint.timestamp
      };
    });
  }, [history]);


  // Calculate chart metrics
  const yAxisDomain = useMemo((): [number, number] => {
    if (!processedChartData?.length) return [0, 0];
    const quotes = processedChartData.map(d => d.quote);
    const min = Math.min(...quotes);
    const max = Math.max(...quotes);
    const padding = (max - min) * 0.1;
    return [Math.max(0, min - padding), max + padding];
  }, [processedChartData]);

  const trendData = useMemo(() => {
    if (!processedChartData?.length) {
      return {
        isPositive: false,
        rateOfChange: 0,
        firstValue: 0,
        lastValue: 0
      };
    }
    
    const firstValue = processedChartData[0]?.quote ?? 0;
    const lastValue = processedChartData[processedChartData.length - 1]?.quote ?? 0;
    const change = firstValue ? Number(((lastValue - firstValue) / firstValue * 100).toFixed(2)) : 0;
    
    return {
      isPositive: lastValue > firstValue,
      rateOfChange: change,
      firstValue,
      lastValue
    };
  }, [processedChartData]);

  // Modified portfolio value effect
  useEffect(() => {
    if (highlights?.total_portfolio_value && 
        prevPortfolioValue !== null && 
        highlights.total_portfolio_value !== prevPortfolioValue) {
      setPrevPortfolioValue(highlights?.total_portfolio_value ?? null);
    }
  }, [highlights?.total_portfolio_value, prevPortfolioValue]);

  // Fetch PnL data with AbortController
  useEffect(() => {
    if (!selectedWallet || selectedWallet === prevPnLWallet) return;
    const controller = new AbortController();
    const fetchPnLData = async () => {
      try {
        dispatch(isLoading({ pnl: true }));
        setPrevPnLWallet(selectedWallet);
        await getPnLData(auth.user.access_token);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching PnL data');
      } finally {
        dispatch(isLoading({ pnl: false }));
      }
    };
    fetchPnLData();
    return () => controller.abort();
  }, [selectedWallet, prevPnLWallet, dispatch, auth.user.access_token]);

  // Fetch portfolio holdings with AbortController
  useEffect(() => {
    if (!selectedWallet || selectedWallet === prevWallet) return;

    const controller = new AbortController();

    const fetchPortfolioData = async () => {
      try {
        dispatch(isLoading({ holdings: true }));
        setPrevWallet(selectedWallet);
        const response = await getPortfolio(auth.user.access_token);
        if (Array.isArray(response?.data)) {
          dispatch(setHoldings(response.data));
        } else {
          dispatch(setHoldings([]));
        }
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('Error fetching portfolio data');
        dispatch(setHoldings([]));
      } finally {
        dispatch(isLoading({ holdings: false }));
      }
    };

    fetchPortfolioData();
    return () => controller.abort();
  }, [selectedWallet, prevWallet, dispatch]);

  // Fetch highlights data with AbortController
  useEffect(() => {
    if (!selectedWallet || selectedWallet === prevWallet) return;

    const controller = new AbortController();

    const fetchHighlights = async () => {
      try {
        dispatch(isLoading({ highlights: true }));
        
        if (!auth.user?.access_token) {
          console.error('Not authenticated');
          return;
        }
    
        const response = await getPortfolioHighlight(auth.user.access_token);
        if (response?.data) {
          const formattedData = {
            ...response.data,
            total_portfolio_value: Number(response.data.total_portfolio_value || 0),
            top_holding_dollar_value: Number(response.data.top_holding_dollar_value || 0),
            asset_count: Number(response.data.asset_count || 0),
            network_count: Number(response.data.network_count || 0),
          };
          
          dispatch(setHighlights(formattedData));
          setLastUpdate(new Date());
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('Error fetching highlights');
        dispatch(setHighlights(null));
      } finally {
        dispatch(isLoading({ highlights: false }));
      }
    };

    fetchHighlights();
    return () => controller.abort();
  }, [selectedWallet, prevWallet, dispatch]);

  const fetchHistoricalData = useCallback(async () => {
    try {
      if (!auth.user?.access_token) {
        console.error('Not authenticated');
        return;
      }
      dispatch(isLoading({ history: true }));
      
      const response = await getPortfolioHistory(auth.user.access_token);
      if (Array.isArray(response?.data)) {
        dispatch(setHistory(response.data));
      } else {
        dispatch(setHistory([]));
      }
    } catch (error) {
      console.error('Error fetching historical data');
      dispatch(setHistory([]));
    } finally {
      dispatch(isLoading({ history: false }));
    }
  }, [auth.user?.access_token, dispatch]);

  useEffect(() => {
    if (!selectedWallet) return;
    fetchHistoricalData();
  }, [selectedWallet, dataDuration, fetchHistoricalData]);

  return (
    <div className={styles.container}>
      <HorizontalWallet />
      <Statistics 
        highlights={highlights}
        expandedCard={expandedCard}
        onCardExpand={handleCardExpand}
        lastUpdate={lastUpdate}
      />
      
      <div style={{ 
        minHeight: '300px',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {loading?.history ? (
          <div className="flex justify-center items-center h-64">
            <Spinner />
          </div>
        ) : processedChartData.length > 0 ? (
          <LineChart 
            loadingState={false}
            title="Portfolio Value"
            showChange={true}
            data={processedChartData}
            flatYAxis={true}
            duration={dataDuration?.id.toString() || "7"}
            yAxisDomain={yAxisDomain}
            trendData={trendData}
            dataLabel="Value"
            showFilters={false}
            onRefresh={fetchHistoricalData}
          />
        ) : null}
      </div>
      <div className={styles.row}>
        <PortfolioHoldings
          loadingState={loading?.holdings ?? false}
          holdings={holdings}
          selectedWallet={selectedWallet}
        />
        <PnLTransactions 
          loadingState={loading?.pnl ?? false}
          pnlList={filteredPnlList}
          selectedWallet={selectedWallet}
          activeIndex={pnlActiveIndex}
          isPaused={pnlIsPaused}
          onSetPaused={setPnlIsPaused}
          onNavigateCards={handlePnLNavigateCards}
        />
      </div>
    </div>
  );
}

export default OverviewPage; 


